import { useState } from "react";
import { useTranslation } from "react-i18next";
import V2Accordion from "./V2Accordion";
import V2MetricRankingSection from "./V2MetricRankingSection";
import V2RecentTrends from "./V2RecentTrends";
import V2TrainabilitySection from "./V2TrainabilitySection";
import {
  DEFAULT_INTERNAL_BLOCK_ORDER,
  INTERNAL_METRICS,
  RANKING_METRICS,
  normalizeInternalBlockOrder,
} from "./v2OverviewConstants";

const DEFAULT_OPEN = {
  trainability: true,
  fatigue: false,
  intensity: false,
  rii: false,
  load: false,
  mli: false,
  trends: true,
};

export default function V2InternalLoadPanel({
  players,
  selectedPositions,
  trends,
  trendPeriod,
  onTrendPeriodChange,
  clubId,
  date,
  period,
  loading = false,
  trendsLoading = false,
  blockOrder = DEFAULT_INTERNAL_BLOCK_ORDER,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(DEFAULT_OPEN);
  const overloadCount = players.filter((player) => player.zone === "overload").length;

  const toggle = (id) => {
    if (id === "trainability") return;
    setOpen((current) => ({ ...current, [id]: !current[id] }));
  };

  const orderedIds = normalizeInternalBlockOrder(blockOrder);
  const metricsById = Object.fromEntries(INTERNAL_METRICS.map((metric) => [metric.id, metric]));

  const renderBlock = (id) => {
    if (id === "trainability") {
      return (
        <V2Accordion
          id="trainability"
          title={t("v2.overview.trainability")}
          badge={t("v2.overview.players_to_handle_count", { count: overloadCount })}
          badgeTone="danger"
          open
          locked
          loading={loading}
          onToggle={() => {}}
        >
          <V2TrainabilitySection
            players={players}
            clubId={clubId}
            date={date}
            period={period}
            loading={loading}
          />
        </V2Accordion>
      );
    }

    if (id === "trends") {
      return (
        <V2Accordion
          id="trends"
          title={t("v2.overview.trends.title")}
          help={t("v2.overview.trends.help")}
          badge={t("v2.overview.trends.selected_count", { count: Math.min(3, players.length) })}
          open={open.trends}
          loading={trendsLoading}
          onToggle={() => toggle("trends")}
        >
          <V2RecentTrends
            players={players}
            selectedPositions={selectedPositions}
            trends={trends}
            period={trendPeriod}
            onPeriodChange={onTrendPeriodChange}
            clubId={clubId}
            loading={trendsLoading}
          />
        </V2Accordion>
      );
    }

    const metric = metricsById[id];
    if (!metric) return null;
    return (
      <V2Accordion
        id={metric.id}
        title={t(metric.titleKey)}
        help={t(metric.helpKey)}
        badge={t(RANKING_METRICS[metric.id].badgeKey)}
        open={open[metric.id]}
        loading={loading}
        onToggle={() => toggle(metric.id)}
      >
        <V2MetricRankingSection
          players={players}
          metricKey={metric.key}
          unit={RANKING_METRICS[metric.id].unit}
          digits={RANKING_METRICS[metric.id].digits}
          deltaDigits={RANKING_METRICS[metric.id].deltaDigits}
          clubId={clubId}
          loading={loading}
        />
      </V2Accordion>
    );
  };

  return (
    <div className="mt-[34px]">
      <div className="mb-[9px] flex items-end justify-between gap-4">
        <div>
          <h2 className="m-0 font-display text-[20px] uppercase tracking-[0.03em] text-[#f4f2ff]">
            {t("v2.overview.internal_indicators")}
          </h2>
          <p className="mt-1 text-[13px] text-[#8b86bc]">{t("v2.overview.internal_indicators_desc")}</p>
        </div>
      </div>

      <div className="mt-2.5 grid gap-3">
        {orderedIds.map((id) => (
          <div key={id}>{renderBlock(id)}</div>
        ))}
      </div>
    </div>
  );
}
